import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  useTheme,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  ListItemIcon,
} from "@mui/material";
import variables from "../../../assets/styles/variables.module.scss";
import CloseIcon from "@mui/icons-material/Close";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import CalculatorTools from "./CalculatorTool";
import Crawl4AITools from "./Crawl4AITool";
import CSVTools from "./CSVTool";
import PostgresTools from "./PostgresTool";
import SQLTools from "./SQLTool";
import SharePointTools from "./SharePointTool";
import Tool, {
  CalculatorTool,
  PostgresTool,
  SQLTool,
} from "../../../Shared-ui/Types/Tool";
import { ToolsKitTypeEnum } from "../../../Shared-ui/Enum/ToolsEnum";
import BuildIcon from "@mui/icons-material/Build";
import CalculateIcon from "@mui/icons-material/Calculate";
import LanguageIcon from "@mui/icons-material/Language";
import TableChartIcon from "@mui/icons-material/TableChart";
import StorageIcon from "@mui/icons-material/Storage";
import DatabaseIcon from "@mui/icons-material/Storage";
import CloudIcon from "@mui/icons-material/Cloud";

interface MyComponentProps {
  open: boolean;
  onClose: () => void;
  tool: any;
}

const CreateTool: React.FC<MyComponentProps> = (props) => {
  const tabConfig = [
    {
      label: "Toolkit",
      icon: <BuildIcon />,
      subTabs: [
        // { name: "Calculator", id:ToolsKitTypeEnum.Calculator, icon: <CalculateIcon /> },
        // { name: "Crawl4AI", id:ToolsKitTypeEnum.Crawl4AI, icon: <LanguageIcon /> },
        // { name: "CSV", id:ToolsKitTypeEnum.Csv, icon: <TableChartIcon /> },
        {
          name: "Postgres",
          id: ToolsKitTypeEnum.Postgres,
          icon: <StorageIcon />,
        },
        { name: "SQL", id: ToolsKitTypeEnum.Sql, icon: <DatabaseIcon /> },
        { name: "SharePoint", id: ToolsKitTypeEnum.SharePoint, icon: <CloudIcon /> },
      ],
    },
    {
      label: "",
      icon: null,
      subTabs: [],
    },
  ];

  const theme = useTheme();
  const [openTabs, setOpenTabs] = useState<Record<string, boolean>>({
    Toolkit: true,
  });
  const [selected, setSelected] = useState<{
    tab: string | null;
    subTab: string | null;
  }>({ tab: "Toolkit", subTab: ToolsKitTypeEnum.Postgres });

  useEffect(() => {
    if (props.open) {
      setSelected({
        tab: "Toolkit",
        subTab: props.tool?.config?.toolkit_type || ToolsKitTypeEnum.Postgres,
      });
      setOpenTabs({ Toolkit: true });
    }
  }, [props.open]);

  const handleTabClick = (clickedLabel: string) => {
    setOpenTabs((prev) => {
      // If the clicked tab is already open, toggle it (collapse)
      if (prev[clickedLabel]) {
        return { ...prev, [clickedLabel]: false };
      }

      // Close all other tabs and open the clicked one
      const newState: Record<string, boolean> = {};
      Object.keys(prev).forEach((key) => {
        newState[key] = false;
      });
      newState[clickedLabel] = true;
      return newState;
    });
  };
  const handleClose = () => {
    props.onClose();
    // handleTabClick(ToolsKitTypeEnum.Calculator);
  };

  const handleSubTabClick = (tab: string, subTab: string) => {
    setSelected({ tab, subTab });
  };

  const renderContent = () => {
    switch (selected.subTab) {
      case ToolsKitTypeEnum.Crawl4AI:
        return <Crawl4AITools handleClose={handleClose} tool={props.tool} />;
      case ToolsKitTypeEnum.Csv:
        return <CSVTools handleClose={handleClose} />;
      case ToolsKitTypeEnum.Postgres:
        return <PostgresTools handleClose={handleClose} tool={props.tool} />;
      case ToolsKitTypeEnum.Sql:
        return <SQLTools handleClose={handleClose} tool={props.tool} />;
      case ToolsKitTypeEnum.SharePoint:
        return <SharePointTools handleClose={handleClose} tool={props.tool} />;
      case ToolsKitTypeEnum.Calculator:
      default:
        return <PostgresTools handleClose={handleClose} tool={props.tool} />;
    }
  };

  const selectedSubTabName =
  tabConfig
    .find((tab) => tab.label === selected.tab)
    ?.subTabs.find((sub) => sub.id === selected.subTab)?.name || "";

  // const dialogTitle = props.tool ? "Tool Settings" : "Create a New Tool";

  return (
    <Dialog open={props.open} onClose={props.onClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: theme.spacing(2),
        }}
      >
        <Box>
          {props.tool ? "Connection Settings" : "Create a New Connection"}
          <Typography
            variant="subtitle2"
            color="textSecondary"
            sx={{ mt: 0.5 }}
          >
            Configure the Connection that your agents can use to perform tasks.
          </Typography>
        </Box>
        <IconButton onClick={props.onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box
          sx={{
            display: "flex",
            minHeight: 300,
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          {/* Sidebar Tabs */}
          <Box
            sx={{
              width: { xs: "100%", md: 200 },
              borderRight: { xs: "none", md: "solid 1px #E3E3E3" } ,
              borderColor: variables.borderColor,
              pr: 2,
            }}
          >
            <Box>
              <Typography variant="h6">Tool Type</Typography>
            </Box>
            <List component="nav">
              {tabConfig.map((tab) => {
                const isParentOpen = openTabs[tab.label];
                const isParentSelected = selected.tab === tab.label;

                return (
                  <Box key={tab.label}>
                    <ListItemButton
                      onClick={() => handleTabClick(tab.label)}
                      sx={{
                        color: isParentOpen ? "primary.main" : "text.primary",
                        fontWeight: isParentOpen ? "bold" : "normal",
                        borderRadius: 1,
                        backgroundColor: isParentOpen ? "action.selected" : "transparent",
                      }}
                    >
                      {tab.icon && (
                        <ListItemIcon
                          sx={{
                            minWidth: 40,
                            color: isParentOpen
                              ? "primary.main"
                              : "text.primary",
                          }}
                        >
                          {tab.icon}
                        </ListItemIcon>
                      )}
                      <ListItemText primary={tab.label} />
                      {tab.subTabs.length > 0 &&
                        (isParentOpen ? <ExpandLess /> : <ExpandMore />)}
                    </ListItemButton>

                    <Collapse in={isParentOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding sx={{ pl: 2 }}>
                        {tab.subTabs.map((subTab) => {
                          const isSelected =
                            selected.tab === tab.label &&
                            selected.subTab === subTab.id;
                          return (
                            <ListItemButton
                              key={subTab.name}
                              sx={{
                                pl: 2,
                                py: 0,
                                mt: 0.5,
                                borderRadius: 1,
                                color: isSelected
                                  ? "primary.main"
                                  : "text.secondary",
                                fontWeight: isSelected ? "bold" : "normal",
                                backgroundColor: "transparent",
                                "&.Mui-selected": {
                                  backgroundColor: "transparent",
                                },
                                "&:hover": {
                                  backgroundColor: "action.hover",
                                },
                              }}
                              selected={isSelected}
                              disabled={(props.tool && props.tool.config?.toolkit_type !== subTab.id) || false}
                              onClick={() =>
                                handleSubTabClick(tab.label, subTab.id)
                              }
                            >
                              <ListItemIcon
                                sx={{
                                  minWidth: 40,
                                  color: isSelected
                                    ? "primary.main"
                                    : "text.secondary",
                                }}
                              >
                                {subTab.icon}
                              </ListItemIcon>
                              <ListItemText primary={subTab.name} />
                            </ListItemButton>
                          );
                        })}
                      </List>
                    </Collapse>
                  </Box>
                );
              })}
            </List>
          </Box>

          {/* Content Area */}
          <Box
            sx={{
              flex: 1,
              px: { xs: 0, md: 3 },
              height: "50vh",
              maxHeight: {xs: "auto", md: "760px"} ,
              overflow: "auto",
            }}
          >
            <Typography
              variant="h6"
              sx={{ display: { xs: "block", md: "none" }, mb: 2 }}
            >
              {selectedSubTabName}
            </Typography>
            {renderContent()}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CreateTool;
